import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Menu, X, MapPin, Search, CreditCard, Ban, Lock, Smile,
  Footprints, Coffee, Users, UtensilsCrossed, Shirt, Dumbbell,
  Scissors, Home as HomeIcon, Wrench, Calendar, Sparkles,
  ChevronRight, ChevronLeft, Facebook, Instagram, Music,
  ArrowRight, Store, Quote, Plus, Building2, PartyPopper, Clock,
  Crown, Trophy, Camera, Eye,
} from "lucide-react";
import { api } from "@/lib/api";
import UserBadges from "@/components/UserBadges";
import CommunityTankSection from "@/components/CommunityTankSection";
import EventPromoModal from "@/components/EventPromoModal";
import SplashHollerStrip from "@/components/SplashHollerStrip";
import { proxyAsset } from "@/lib/proxyAsset";
import "@/styles/splash-met.css";

/* Map "icon" string from settings → lucide-react component. Keeps the
   admin editor a simple <select> instead of free-form code. */
const ICONS = {
  Ban, Lock, Smile, Search, MapPin, CreditCard, Footprints, Coffee, Users,
  UtensilsCrossed, Shirt, Dumbbell, Scissors, Home: HomeIcon, Wrench,
  Calendar, Sparkles, Store, Music, Facebook, Instagram, Building2, PartyPopper,
};
function Icon({ name, ...rest }) {
  const C = ICONS[name] || Sparkles;
  return <C {...rest} />;
}

/**
 * Public splash / landing page (`/`).
 *
 * 100% admin-editable via /api/splash/settings. Live numbers from
 * /api/splash/stats and the daily quote from /api/daily-quote.
 *
 * Design = polished dark hero → torn paper → cream "How It Works" →
 * dark founder section → cream winner carousel → dark dual CTA →
 * daily-quote band → dark footer with socials.
 */
export default function SplashPage() {
  const [s, setS] = useState(null);
  const [stats, setStats] = useState(null);
  const [winners, setWinners] = useState(null);
  const [quote, setQuote] = useState(null);
  const [lm, setLm] = useState(null);
  const [missions, setMissions] = useState(null);
  const [missionStats, setMissionStats] = useState(null);
  const [metSnap, setMetSnap] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/splash/settings").then(({ data }) => { if (!cancelled) setS(data); }).catch(() => { if (!cancelled) setS({}); });
    api.get("/splash/stats").then(({ data }) => { if (!cancelled) setStats(data); }).catch(() => { if (!cancelled) setStats({}); });
    api.get("/winners?limit=18").then(({ data }) => {
      if (cancelled) return;
      setWinners(Array.isArray(data?.winners) ? data.winners : Array.isArray(data) ? data : []);
    }).catch(() => { if (!cancelled) setWinners([]); });
    api.get("/daily-quote").then(({ data }) => { if (!cancelled) setQuote(data); }).catch(() => { if (!cancelled) setQuote(null); });
    api.get("/lootmaster/settings").then(({ data }) => { if (!cancelled) setLm(data); }).catch(() => { if (!cancelled) setLm(null); });
    api.get("/missions/settings").then(({ data }) => { if (!cancelled) setMissions(data); }).catch(() => { if (!cancelled) setMissions(null); });
    api.get("/missions/stats").then(({ data }) => { if (!cancelled) setMissionStats(data); }).catch(() => { if (!cancelled) setMissionStats(null); });
    api.get("/founder/met-snapshot").then(({ data }) => { if (!cancelled) setMetSnap(data); }).catch(() => { if (!cancelled) setMetSnap(null); });
    return () => { cancelled = true; };
  }, []);

  if (!s) {
    return (
      <div className="splash-root flex items-center justify-center" data-testid="splash-page">
        <div className="splash-mono text-sm opacity-60">Loading the loot…</div>
      </div>
    );
  }

  return (
    <div className="splash-root" data-testid="splash-page">
      <EventPromoModal />
      <Header s={s} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Hero s={s} stats={stats} />
      <div className="torn-paper-down" aria-hidden="true" />
      <HowItWorks s={s} />
      <ClaimCTA s={s} />
      <div className="torn-paper-up" aria-hidden="true" />
      <WhySection s={s} />
      <LiveTicker s={s} stats={stats} />
      <CommunityTankSection variant="cream" />
      <RecentWinners s={s} winners={winners} />
      <SplashHollerStrip />
      <SplashSponsorSpotlight s={s} />
      <MetFounderSection s={s} snap={metSnap} />
      <HostPrivateHunt s={s} />
      <DualCTA s={s} />
      <MissionsCTA missions={missions} missionStats={missionStats} />
      <LootMasterCTA lm={lm} />
      <FAQSection s={s} />
      <QuoteBand s={s} quote={quote} />
      <Footer s={s} />
      <MobileStickyCTA s={s} />
    </div>
  );
}

/* ===================================================================== */
/*                              HEADER                                    */
/* ===================================================================== */

function Header({ s, menuOpen, setMenuOpen }) {
  const links = Array.isArray(s.nav_links) ? s.nav_links : [];
  return (
    <header className="splash-header" data-testid="splash-header">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="splash-header-logo">
          {s.logo_image_url ? (
            <img src={proxyAsset(s.logo_image_url)} alt={s.logo_alt || "The Local Loot"} className="h-12 md:h-14 w-auto" />
          ) : (
            <span className="splash-display text-2xl text-[#76B82A]">THE LOCAL LOOT</span>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l, i) => (
            <Link
              key={`${l.href}-${i}`}
              to={l.href}
              className={`splash-nav-link${l.href === "/" ? " is-active" : ""}`}
              data-testid={`splash-nav-${i}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setMenuOpen((v) => !v)}
          className="lg:hidden p-2 border border-[#76B82A]/40 text-[#f7f3e6]"
          data-testid="splash-menu-toggle"
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {menuOpen && (
        <div className="lg:hidden bg-[#0a0d09] border-t border-[#76B82A]/25" data-testid="splash-mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {links.map((l, i) => (
              <Link
                key={`m-${l.href}-${i}`}
                to={l.href}
                onClick={() => setMenuOpen(false)}
                className="splash-nav-link py-3 border-b border-[#f7f3e6]/10"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

/* ===================================================================== */
/*                                HERO                                    */
/* ===================================================================== */

function Hero({ s, stats }) {
  const badges = Array.isArray(s.hero_trust_badges) ? s.hero_trust_badges : [];
  const chalk = Array.isArray(s.hero_chalk_lines) ? s.hero_chalk_lines : [];
  return (
    <section className="splash-hero relative" data-testid="splash-hero">
      {s.hero_bg_image_url && (
        <div
          className="splash-hero-bg"
          style={{ backgroundImage: `url(${proxyAsset(s.hero_bg_image_url)})` }}
          aria-hidden="true"
        />
      )}
      <div className="splash-hero-overlay" aria-hidden="true" />
      <div className="splash-grain" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-16 md:pb-24 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        {/* LEFT — copy */}
        <div className="lg:col-span-7">
          {(stats?.online ?? 0) > 0 && (
            <div className="mb-5 inline-flex">
              <span className="live-chip-light">
                <span className="live-chip-dot-green" />
                {stats.online} hunter{stats.online === 1 ? "" : "s"} online right now
              </span>
            </div>
          )}

          <h1 className="splash-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] mt-2 leading-[0.95]">
            <span className="block headline-stamp">{s.hero_headline_line1 || "REAL CLUES."}</span>
            <span className="block headline-stamp mt-1 md:mt-2">{s.hero_headline_line2 || "REAL PLACES."}</span>
            <span className="block headline-stamp--green mt-1 md:mt-2">{s.hero_headline_line3 || "REAL PRIZES."}</span>
          </h1>

          <p className="mt-6 splash-display text-lg md:text-xl text-[#f7f3e6]">
            {s.hero_kicker}
          </p>
          <p className="mt-3 max-w-xl text-[15px] md:text-base text-[#f7f3e6]/70 leading-relaxed">
            {s.hero_body}
          </p>

          {badges.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
              {badges.map((b, i) => (
                <span key={i} className="trust-badge" data-testid={`splash-trust-${i}`}>
                  <Icon name={b.icon} size={18} />
                  {b.label}
                </span>
              ))}
            </div>
          )}

          {s.hero_tagline && (
            <div className="mt-5 tagline-script text-3xl md:text-4xl">
              {s.hero_tagline}
            </div>
          )}

          <div className="mt-7 md:mt-9 flex flex-wrap items-center gap-4">
            <Link
              to={s.hero_primary_cta_href || "/home"}
              className="btn-loot-solid"
              data-testid="splash-start-hunting-btn"
            >
              {s.hero_primary_cta_label || "Start hunting"}
              <MapPin size={18} />
            </Link>
            <Link
              to={s.hero_secondary_cta_href || "/register"}
              className="btn-loot-outline"
              data-testid="splash-secondary-btn"
            >
              {s.hero_secondary_cta_label || "Register now"}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>

        {/* RIGHT — card visual */}
        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto max-w-sm md:max-w-md">
            <div className="loot-card-hero card-float">
              <div className="loot-card-hero-inner">
                {s.hero_card_image_url ? (
                  <img src={proxyAsset(s.hero_card_image_url)} alt={s.logo_alt || "The Local Loot"} />
                ) : null}
              </div>
            </div>
            {/* Floating crayon arrow */}
            <div className="hidden md:block absolute -bottom-6 -left-2 crayon-arrow-green text-3xl">
              free · no catch ↗
            </div>
          </div>
        </div>
      </div>

      {/* Manifesto strip — admin-editable chant rendered as a single
          horizontal printed slogan bar across the full hero width. Replaces
          the old absolute-positioned chalk card that overlapped the loot
          image on tablet/desktop. Renders nothing if no chalk lines set. */}
      {chalk.length > 0 && (
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pb-12 md:pb-16">
          <div
            className="manifesto-strip flex items-center gap-3 md:gap-5 overflow-x-auto whitespace-nowrap py-3 md:py-4 px-4 md:px-6 border-y-2 border-[#76B82A]/30"
            data-testid="splash-chalk-panel"
            aria-label="The Local Loot manifesto"
          >
            {chalk.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span aria-hidden="true" className="splash-display text-[#76B82A]/70 text-2xl md:text-3xl shrink-0">·</span>
                )}
                <span className="splash-display text-[#f7f3e6] text-2xl md:text-3xl lg:text-4xl tracking-tight uppercase shrink-0">
                  {line}
                </span>
              </React.Fragment>
            ))}
            <span aria-hidden="true" className="splash-script text-[#76B82A] text-3xl md:text-4xl shrink-0 ml-2 md:ml-3">
              {s.hero_chalk_arrow || "↘"}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

/* ===================================================================== */
/*                          HOW IT WORKS                                  */
/* ===================================================================== */

function HowItWorks({ s }) {
  const steps = Array.isArray(s.how_steps) ? s.how_steps : [];
  // Optional admin-editable bullet list per step. Falls back to the body
  // text if the new field isn't populated yet — fully back-compat.
  return (
    <section className="cream-section pt-10 md:pt-16 pb-12 md:pb-16" data-testid="splash-how">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#4f8c19]">
            {s.how_overline_kicker || "Confused? It's simpler than it looks."}
          </div>
          <h2 className="splash-display text-4xl md:text-5xl uppercase mt-2">
            {s.how_overline || "How it works"}
          </h2>
          {s.how_intro && (
            <p className="mt-3 text-[15px] md:text-base text-[#0d100d]/75 leading-relaxed">
              {s.how_intro}
            </p>
          )}
        </div>

        <div className="mt-10 md:mt-14 grid md:grid-cols-3 gap-8 md:gap-10">
          {steps.map((step, i) => {
            const bullets = Array.isArray(step.bullets) ? step.bullets.filter(Boolean) : [];
            return (
              <article
                key={i}
                className="bg-[#fffaef] border-2 border-[#0d100d] p-6 md:p-7 shadow-[6px_6px_0_#0d100d] flex flex-col"
                data-testid={`splash-how-step-${i}`}
              >
                <div className="flex items-center gap-3">
                  <span className="step-num">{step.n || String(i + 1)}</span>
                  <span className="step-icon">
                    <Icon name={step.icon} size={22} />
                  </span>
                </div>
                <h3 className="splash-display text-2xl md:text-3xl uppercase mt-4 leading-tight">
                  {step.title}
                </h3>
                <p className="mt-2 text-[15px] text-[#0d100d]/80 leading-relaxed">
                  {step.body}
                </p>
                {bullets.length > 0 && (
                  <ul className="mt-4 space-y-2 border-t border-[#0d100d]/15 pt-3">
                    {bullets.map((b, bi) => (
                      <li key={bi} className="flex gap-2 text-[14px] text-[#0d100d]/80 leading-snug">
                        <span className="mt-[6px] w-1.5 h-1.5 rounded-full bg-[#76B82A] shrink-0" aria-hidden="true" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {step.tip && (
                  <div className="mt-4 splash-script text-lg text-[#4f8c19] leading-snug">
                    💡 {step.tip}
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="text-center mt-10 md:mt-12 flex flex-col items-center gap-5">
          {s.how_banner && (
            <div className="banner-pill" data-testid="splash-how-banner">
              {s.how_banner}
            </div>
          )}
          <Link
            to={s.how_more_href || "/how-it-works"}
            className="btn-paper-light"
            data-testid="splash-how-more-btn"
          >
            {s.how_more_label || "See the full how-it-works guide"}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                             CLAIM CTA                                  */
/* ===================================================================== */

function ClaimCTA({ s }) {
  return (
    <section className="cream-section pt-2 pb-14 md:pb-20" data-testid="splash-claim-cta">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <div className="inline-flex flex-col items-center gap-4 p-6 md:p-10 border-2 border-[#0d100d] bg-[#fffaef] shadow-[8px_8px_0_#0d100d]">
          {s.claim_overline && (
            <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#c8331f]">
              {s.claim_overline}
            </div>
          )}
          <h2 className="splash-display text-4xl md:text-6xl uppercase">
            {(s.claim_headline || "CLAIM MY LOOT").split(" ").map((w, i, arr) => (
              <span key={i}>
                {i === arr.length - 1 ? <span style={{ color: "#76B82A" }}>{w}</span> : w}
                {i < arr.length - 1 ? " " : ""}
              </span>
            ))}
          </h2>
          {s.claim_subhead && (
            <p className="text-[15px] md:text-base text-[#0d100d]/75 max-w-xl">
              {s.claim_subhead}
            </p>
          )}
          <Link
            to={s.claim_cta_href || "/claim"}
            className="btn-paper-light mt-1"
            data-testid="splash-claim-loot-btn"
          >
            {s.claim_cta_label || "Claim my loot"}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                             WHY SECTION                                */
/* ===================================================================== */

function WhySection({ s }) {
  const icons = Array.isArray(s.why_icons) ? s.why_icons : [];
  return (
    <section className="relative bg-[#0d100d] py-20 md:py-28" data-testid="splash-why">
      <div className="splash-grain" aria-hidden="true" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <div className="lg:col-span-4">
          {s.why_image_url ? (
            <div
              className="w-full aspect-[3/4] bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${proxyAsset(s.why_image_url)})` }}
              data-testid="splash-why-img"
              aria-label="Mickey Trivett"
            />
          ) : (
            <div className="w-full aspect-[3/4] bg-[#1a1d18] flex items-center justify-center text-[#f7f3e6]/40 splash-mono text-xs">
              Founder photo
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <h2 className="splash-display text-3xl md:text-5xl uppercase">
            {s.why_overline || "Why The Local Loot exists"}
          </h2>
          <div className="mt-3 splash-script text-2xl text-[#76B82A]">
            {s.why_intro}
          </div>
          <p className="mt-5 text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed max-w-xl">
            {s.why_body}
          </p>
          {icons.length > 0 && (
            <div className="mt-8 grid grid-cols-3 gap-4 md:gap-6 max-w-xl">
              {icons.map((it, i) => (
                <div key={i} className="text-center" data-testid={`splash-why-icon-${i}`}>
                  <div className="gold-icon flex justify-center">
                    <Icon name={it.icon} size={36} />
                  </div>
                  <div className="mt-3 text-[12px] md:text-[13px] text-[#f7f3e6]/80 leading-snug">
                    {it.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-3">
          <div className="why-quote-box">
            <h3 className="splash-display text-2xl md:text-3xl uppercase leading-tight">
              {s.why_quote_title}
            </h3>
            <p className="mt-3 splash-script text-2xl text-[#f7f3e6]/95 leading-snug">
              {s.why_quote_body}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                           LIVE TICKER                                  */
/* ===================================================================== */

// Roll-up effect for stat numbers — animates from 0 → target when the
// element scrolls into view. Pure CSS would be possible but JS guarantees
// it works after data lands asynchronously.
function CountUp({ value, duration = 1200 }) {
  const [n, setN] = useState(0);
  const [seen, setSeen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setSeen(true); });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!seen || typeof value !== "number") return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, value, duration]);

  if (typeof value !== "number") return <span ref={ref} className="count-up">—</span>;
  return <span ref={ref} className="count-up">{n.toLocaleString()}</span>;
}

function CountdownChip({ s, etaSeconds }) {
  const [remaining, setRemaining] = useState(etaSeconds);
  useEffect(() => { setRemaining(etaSeconds); }, [etaSeconds]);
  useEffect(() => {
    if (typeof remaining !== "number" || remaining <= 0) return;
    const id = setInterval(() => setRemaining((r) => (typeof r === "number" ? Math.max(0, r - 1) : r)), 1000);
    return () => clearInterval(id);
  }, [remaining !== null && remaining !== undefined]);
  if (typeof remaining !== "number" || remaining <= 0) {
    if (s.ticker_countdown_idle_label) {
      return (
        <span className="countdown-chip" data-testid="splash-countdown-idle">
          <Clock size={14} className="text-[#d7a62a]" />
          {s.ticker_countdown_idle_label}
        </span>
      );
    }
    return null;
  }
  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const sec = remaining % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const formatted = d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(h)}:${pad(m)}:${pad(sec)}`;
  return (
    <span className="countdown-chip" data-testid="splash-countdown">
      <Clock size={14} className="text-[#d7a62a]" />
      <span className="splash-mono uppercase tracking-widest text-[10px]">
        {s.ticker_countdown_overline || "Next drop in"}
      </span>
      <span className="countdown-chip-num">{formatted}</span>
    </span>
  );
}

function LiveTicker({ s, stats }) {
  if (s.ticker_show === false) return null;
  const items = Array.isArray(s.ticker_items) ? s.ticker_items : [];
  if (items.length === 0) return null;
  return (
    <section className="bg-[#0d100d] border-t border-[#76B82A]/15 border-b border-[#76B82A]/15 py-10" data-testid="splash-ticker">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row md:items-end gap-6">
        <div className="md:flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A]">
              {s.ticker_overline}
            </div>
            {s.ticker_countdown_show !== false && (
              <CountdownChip s={s} etaSeconds={stats?.next_drop_eta_seconds} />
            )}
          </div>
          <div className={`mt-3 grid gap-4 md:gap-6 ${items.length === 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3"}`}>
            {items.map((it, i) => (
              <div key={i} data-testid={`splash-ticker-${i}`}>
                <div className="splash-display text-4xl md:text-5xl text-[#f7f3e6]">
                  <CountUp value={(stats || {})[it.key]} />
                </div>
                <div className="mt-1 text-[12px] md:text-sm text-[#f7f3e6]/65 uppercase tracking-wider">
                  {it.label}
                </div>
              </div>
            ))}
          </div>
        </div>
        {s.ticker_cta_label && (
          <Link
            to={s.ticker_cta_href || "/register"}
            className="btn-loot-solid self-start md:self-end"
            data-testid="splash-ticker-cta"
          >
            {s.ticker_cta_label}
            <ChevronRight size={16} />
          </Link>
        )}
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                       HOST A PRIVATE HUNT                              */
/* ===================================================================== */

function MetFounderSection({ s, snap }) {
  // Hooks MUST be called before any early return — rules-of-hooks.
  const [now, setNow] = useState(Date.now());
  const isOnSite = snap?.presence_mode === "on_site";
  const onSiteSince = snap?.on_site_since;
  useEffect(() => {
    if (!isOnSite || !onSiteSince) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isOnSite, onSiteSince]);

  if (s.met_founder_show === false) return null;

  const photo = snap?.founder_photo_url || s.met_founder_photo_url || "";
  const founderName = snap?.founder_name || s.met_founder_name || "Mickey";
  const stats = snap?.stats || { total_meets: 0, cities: 0, hunters: 0 };
  const onSiteCity = snap?.presence_city || "";
  const durationHours = Number(snap?.on_site_duration_hours || 3);
  const locationNote = snap?.presence_location_note || "";

  // Compute remaining countdown
  let remainingMs = 0;
  let liveActive = false;
  if (isOnSite && onSiteSince) {
    const startMs = new Date(onSiteSince).getTime();
    const endMs = startMs + durationHours * 3600 * 1000;
    remainingMs = Math.max(0, endMs - now);
    liveActive = remainingMs > 0;
  }
  const hh = Math.floor(remainingMs / 3600000);
  const mm = Math.floor((remainingMs % 3600000) / 60000);
  const ss = Math.floor((remainingMs % 60000) / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  const countdownText = hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;

  const eyebrow = s.met_founder_eyebrow || "Meet the founder";
  const headline = s.met_founder_headline || "Catch me in the wild.";
  const kicker = s.met_founder_kicker || `That's me — ${founderName}.`;
  const body = s.met_founder_body
    || "I'm out in the Tri-Cities most days. Coffee shops, trailheads, sponsor visits, dropping loot. If you spot me — say hi, mention The Local Loot, and you'll earn a legendary badge that money literally can't buy.";
  const ctaLabel = s.met_founder_cta_label || "Claim your meet →";
  const liveLabelTemplate = s.met_founder_live_label
    || `${founderName.toUpperCase()} IS LIVE${onSiteCity ? ` IN ${onSiteCity.toUpperCase()}` : ""} — RIGHT NOW`;

  const tiers = [
    { n: 1, t: s.met_founder_tier_1 || "Found the Founder — first meet, anywhere, any time." },
    { n: 2, t: s.met_founder_tier_2 || "Loot Disciple — met me in 3 different cities." },
    { n: 3, t: s.met_founder_tier_3 || "Local Legend — met me in 10 different cities. True road dog." },
  ];

  return (
    <section className="splash-met-section" data-testid="splash-met-founder">
      <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Photo side */}
        <div className="lg:col-span-5 flex justify-center lg:justify-start order-1 lg:order-1">
          <div className="splash-met-photo-wrap">
            <div className="splash-met-stamp">{liveActive ? "Live · NOW" : "Wanted · Reward"}</div>
            <div className="splash-met-photo" data-testid="splash-met-photo">
              {photo ? (
                <img src={proxyAsset(photo)} alt={founderName} />
              ) : (
                <span className="placeholder">{founderName[0]}</span>
              )}
            </div>
          </div>
        </div>

        {/* Copy side */}
        <div className="lg:col-span-7 order-2 lg:order-2">
          {/* LIVE BURST — green pulsing pill, only when on_site + timer not expired */}
          {liveActive && (
            <Link
              to="/met-the-founder"
              className="splash-met-live-burst no-underline"
              data-testid="splash-met-live-burst"
            >
              <div className="splash-met-live-content">
                <div className="splash-met-live-eyebrow">
                  <span className="live-dot" /> LIVE NOW
                </div>
                <div className="splash-met-live-headline">
                  🎯 {liveLabelTemplate}
                </div>
                {locationNote && (
                  <div className="splash-met-live-sub">
                    📍 {locationNote}
                  </div>
                )}
              </div>
              <div className="splash-met-countdown" data-testid="splash-met-countdown">
                <div className="splash-met-countdown-num">{countdownText}</div>
                <div className="splash-met-countdown-label">remaining</div>
              </div>
            </Link>
          )}

          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
            {eyebrow}
          </div>
          <h2 className="splash-display text-4xl md:text-6xl uppercase mt-2 leading-[0.95]">
            {headline}
          </h2>
          {kicker && (
            <div className="splash-script text-2xl md:text-3xl text-[#d7a62a] mt-2">
              {kicker}
            </div>
          )}
          <p className="mt-4 max-w-xl text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed whitespace-pre-line">
            {body}
          </p>

          {/* Presence + stats inline (hidden when live burst already visible to avoid duplication) */}
          {!liveActive && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {isOnSite ? (
                <div className="splash-met-presence" data-testid="splash-met-presence-on">
                  <span className="dot" /> Spotted now {onSiteCity ? `· ${onSiteCity}` : ""}
                </div>
              ) : (
                <div className="splash-met-presence off" data-testid="splash-met-presence-off">
                  <span className="dot" /> {s.met_founder_offsite_label || "Off the trail today"}
                </div>
              )}
            </div>
          )}
          <div className={`${liveActive ? "mt-6" : "mt-4"} flex items-center gap-4 text-[#f7f3e6]/70 text-sm font-mono flex-wrap`}>
            <span data-testid="splash-met-stat-meets"><Trophy size={12} className="inline mr-1 text-[#d7a62a]" /> <strong className="text-[#d7a62a] text-lg">{stats.total_meets}</strong> meets</span>
            <span data-testid="splash-met-stat-cities"><MapPin size={12} className="inline mr-1 text-[#76B82A]" /> <strong className="text-[#76B82A] text-lg">{stats.cities}</strong> cities</span>
            <span data-testid="splash-met-stat-hunters"><Users size={12} className="inline mr-1 text-[#f7f3e6]" /> <strong className="text-[#f7f3e6] text-lg">{stats.hunters}</strong> hunters</span>
          </div>

          {/* Tier ladder mini */}
          <div className="mt-6 space-y-2 max-w-xl">
            {tiers.map((tier) => (
              <div key={tier.n} className="splash-met-tier" data-tier={String(tier.n)} data-testid={`splash-met-tier-${tier.n}`}>
                <div className="num">{tier.n}</div>
                <div className="text-[14px] text-[#f7f3e6]/90 leading-relaxed">{tier.t}</div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/met-the-founder"
              className="btn-loot inline-flex items-center gap-2"
              data-testid="splash-met-cta"
            >
              <Crown size={16} /> {liveActive ? "FIND HIM NOW →" : ctaLabel}
            </Link>
            {snap?.last_meet && snap.last_meet.username && (
              <Link
                to={`/u/${snap.last_meet.username}`}
                className="btn-loot-outline inline-flex items-center gap-2 text-xs"
                data-testid="splash-met-last"
              >
                <Eye size={14} /> Last meet: @{snap.last_meet.username}{snap.last_meet.city ? ` in ${snap.last_meet.city}` : ""}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


function HostPrivateHunt({ s }) {
  if (s.private_show === false) return null;
  return (
    <section className="private-section py-16 md:py-24" data-testid="splash-private">
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          {s.private_image_url ? (
            <div
              className="w-full aspect-[4/5] bg-cover bg-center bg-no-repeat border-2 border-[#d7a62a]/40 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)]"
              style={{ backgroundImage: `url(${proxyAsset(s.private_image_url)})` }}
              aria-label="Private hunt"
              data-testid="splash-private-img"
            />
          ) : (
            <div className="w-full aspect-[4/5] bg-[#1a1d18] border-2 border-[#d7a62a]/40" />
          )}
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
            {s.private_overline || "Host a private hunt"}
          </div>
          <h2 className="splash-display text-4xl md:text-6xl uppercase mt-2">
            {s.private_headline}
          </h2>
          {s.private_kicker && (
            <div className="splash-script text-2xl md:text-3xl text-[#d7a62a] mt-2">
              {s.private_kicker}
            </div>
          )}
          <p className="mt-4 max-w-xl text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed">
            {s.private_body}
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4 md:gap-5">
            <article className="private-card" data-testid="splash-private-personal">
              <span className="pc-icon"><PartyPopper size={20} /></span>
              <h3 className="splash-display text-2xl uppercase mt-3">{s.private_personal_title}</h3>
              <p className="mt-2 text-[14px] text-[#f7f3e6]/75 leading-relaxed">
                {s.private_personal_body}
              </p>
              <Link
                to={s.private_personal_cta_href || "/private/sales"}
                className="btn-loot-outline mt-4 text-xs"
                data-testid="splash-private-personal-cta"
              >
                {s.private_personal_cta_label || "Plan a personal hunt"}
                <ArrowRight size={14} />
              </Link>
            </article>
            <article className="private-card" data-testid="splash-private-corporate">
              <span className="pc-icon"><Building2 size={20} /></span>
              <h3 className="splash-display text-2xl uppercase mt-3">{s.private_corporate_title}</h3>
              <p className="mt-2 text-[14px] text-[#f7f3e6]/75 leading-relaxed">
                {s.private_corporate_body}
              </p>
              <Link
                to={s.private_corporate_cta_href || "/private/sales"}
                className="btn-loot-outline mt-4 text-xs"
                data-testid="splash-private-corporate-cta"
              >
                {s.private_corporate_cta_label || "Book a corporate hunt"}
                <ArrowRight size={14} />
              </Link>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                           FAQ ACCORDION                                */
/* ===================================================================== */

function FAQSection({ s }) {
  if (s.faq_show === false) return null;
  const items = Array.isArray(s.faq_items) ? s.faq_items : [];
  if (items.length === 0) return null;
  return (
    <section className="faq-section py-14 md:py-20" data-testid="splash-faq">
      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center">
          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#4f8c19]">
            {s.faq_overline || "Quick questions"}
          </div>
          <h2 className="splash-display text-3xl md:text-5xl uppercase mt-2">
            {s.faq_headline || "Stuff people ask before they hunt."}
          </h2>
        </div>
        <div className="mt-8">
          {items.map((it, i) => (
            <div key={i} className="faq-item" data-testid={`splash-faq-${i}`}>
              <details>
                <summary>
                  <span>{it.q}</span>
                  <Plus size={22} className="faq-toggle shrink-0" />
                </summary>
                <div className="faq-body">{it.a}</div>
              </details>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                         RECENT WINNERS                                 */
/* ===================================================================== */

function RecentWinners({ s, winners }) {
  // Merge live winners with the editable fallback list. We prefer real
  // winners; pad with seed winners so we always show 6+ cards.
  const merged = useMemo(() => {
    const live = (winners || []).slice(0, 12).map((w) => {
      // Respect the per-winner hide_amount flag set on the Winners Wall —
      // when true, the splash shows the prize TYPE only (e.g. "Cash", "Gift
      // card") without the dollar value, mirroring /winners + ShareFindModal.
      const hideAmount = !!w.hide_amount;
      const typeLabel = w.prize_custom_label
        || (w.prize_type === "cash" ? "Cash" : (w.prize_type ? `${w.prize_type[0].toUpperCase()}${w.prize_type.slice(1)}` : "Prize"));
      let prize;
      if (w.is_mystery) {
        prize = "Mystery Loot";
      } else if (hideAmount || !w.amount) {
        prize = typeLabel;
      } else {
        const lower = (w.prize_type === "cash") ? "cash" : (w.prize_custom_label || w.prize_type || "prize");
        prize = `$${w.amount} ${lower}`;
      }
      return {
        name: w.name || w.username || "—",
        town: w.city || "",
        prize,
        sponsor: w.sponsor?.business_name || w.sponsor_name || "Local sponsor",
        photo_url: w.photo_url || w.selfie_url || null,
        loot_legend: !!w.user_loot_legend,
        good_deeds: Number(w.user_good_deeds_count || 0),
        igniter: !!w.user_igniter,
        user_badges: Array.isArray(w.user_badges) ? w.user_badges : [],
        _live: true,
      };
    });
    const fallback = (Array.isArray(s.winners_fallback) ? s.winners_fallback : []).map((w) => ({ ...w, _live: false }));
    if (live.length >= 6) return live;
    const out = [...live];
    for (const f of fallback) {
      if (out.length >= 6) break;
      out.push(f);
    }
    return out;
  }, [winners, s.winners_fallback]);

  if (merged.length === 0) return null;

  const scroll = (dir) => {
    const el = document.getElementById("splash-winners-scroll");
    if (!el) return;
    el.scrollBy({ left: dir * 260, behavior: "smooth" });
  };

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const photoUrl = (u) => {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    return `${backendBase}${u}`;
  };

  return (
    <section className="cream-section py-14 md:py-20" data-testid="splash-winners">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="mb-6 md:mb-8 text-center md:text-left">
          <h2 className="splash-display text-3xl md:text-5xl uppercase">
            {s.winners_overline || "Recent winners"}
          </h2>
        </div>

        <div className="relative">
          <button
            type="button"
            aria-label="Previous"
            onClick={() => scroll(-1)}
            className="absolute -left-2 md:-left-4 top-1/2 -translate-y-1/2 z-10 bg-[#0d100d] text-[#f7f3e6] p-2 rounded-full shadow-md hover:bg-[#76B82A] transition"
            data-testid="splash-winners-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <div
            id="splash-winners-scroll"
            className="scroll-x flex gap-4 md:gap-5 pb-3 px-1"
          >
            {merged.map((w, i) => (
              <div key={i} className="winner-card" data-testid={`splash-winner-${i}`}>
                <div
                  className="winner-card-photo"
                  style={w.photo_url ? { backgroundImage: `url(${proxyAsset(photoUrl(w.photo_url))})` } : {}}
                >
                  {!w.photo_url && (
                    <div className="absolute inset-0 flex items-center justify-center splash-display text-3xl text-[#f7f3e6]/65">
                      {(w.name || "").split(" ").map((p) => p[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  {w._live && (
                    <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-[#c8331f] text-[#f7f3e6] splash-mono text-[10px] uppercase tracking-widest">
                      Verified
                    </div>
                  )}
                </div>
                <div className="px-3 py-3">
                  <div className="splash-display text-xl uppercase leading-tight inline-flex items-center gap-2 flex-wrap">
                    {w.name}
                    <UserBadges
                      lootLegend={w.loot_legend}
                      goodDeeds={w.good_deeds}
                      igniter={w.igniter}
                      badges={w.user_badges}
                      testIdPrefix={`splash-winner-badge-${i}`}
                    />
                  </div>
                  <div className="splash-mono text-[11px] text-[#0d100d]/60 mt-0.5">{w.town}</div>
                  <div className="splash-display text-[#76B82A] text-base uppercase mt-2 leading-tight">{w.prize}</div>
                  <div className="text-[11px] text-[#0d100d]/65 mt-0.5 leading-tight">
                    Sponsored by<br /><span className="font-semibold">{w.sponsor}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            aria-label="Next"
            onClick={() => scroll(1)}
            className="absolute -right-2 md:-right-4 top-1/2 -translate-y-1/2 z-10 bg-[#0d100d] text-[#f7f3e6] p-2 rounded-full shadow-md hover:bg-[#76B82A] transition"
            data-testid="splash-winners-next"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="mt-8 flex justify-center">
          <Link
            to={s.winners_view_all_href || "/winners"}
            className="btn-loot-red"
            data-testid="splash-view-all-winners"
          >
            {s.winners_view_all_label || "View all winners"}
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                       SPLASH SPONSOR SPOTLIGHT                          */
/* ===================================================================== */

/**
 * Splash-styled sponsor showcase. Marquee row of every active sponsor
 * (logo/initial card with name, city, package badge), plus a featured
 * spotlight card pulling out the top-tier sponsor. Renders nothing if zero
 * active sponsors. Cream theme to match the surrounding splash sections.
 *
 * Why on splash: paying sponsors deserve visibility on the public-facing
 * marketing page — not just the post-login home. This is a sales receipt
 * for current sponsors AND social proof for would-be sponsors who land on
 * "/" before they sign up.
 */
function SplashSponsorSpotlight({ s }) {
  const [sponsors, setSponsors] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get("/sponsors")
      .then(({ data }) => { if (!cancelled) setSponsors(Array.isArray(data?.sponsors) ? data.sponsors : []); })
      .catch(() => { if (!cancelled) setSponsors([]); });
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate the featured card every ~6s
  useEffect(() => {
    if (!sponsors || sponsors.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % sponsors.length), 6000);
    return () => clearInterval(t);
  }, [sponsors]);

  if (!sponsors || sponsors.length === 0) return null;

  const featured = sponsors[idx % sponsors.length];
  const featuredAccent = featured.package === "city_takeover" ? "#d7a62a" : "#76B82A";

  return (
    <section
      className="cream-section py-14 md:py-20"
      data-testid="splash-sponsor-spotlight"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center">
          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
            {s.sponsor_spotlight_overline || "Sponsor spotlight"}
          </div>
          <h2 className="splash-display text-3xl md:text-5xl uppercase mt-2 leading-[0.95]">
            {s.sponsor_spotlight_headline || "The locals making this possible."}
          </h2>
          <p className="mt-3 max-w-xl mx-auto text-[14px] md:text-[15px] text-[#0d100d]/70">
            {s.sponsor_spotlight_body
              || "Every drop is funded by a real business in your community. Here's who's loading the loot this week."}
          </p>
        </div>

        {/* Featured sponsor card — auto-rotating */}
        <article
          className="mt-8 md:mt-10 bg-[#fffaef] border-2 border-[#0d100d] shadow-[8px_8px_0_#0d100d] grid md:grid-cols-12 gap-0"
          data-testid={`splash-sponsor-featured-${featured.id || idx}`}
        >
          {/* Logo / initial slab */}
          <div
            className="md:col-span-4 aspect-square md:aspect-auto bg-cover bg-center bg-no-repeat border-b-2 md:border-b-0 md:border-r-2 border-[#0d100d] flex items-center justify-center"
            style={{
              backgroundImage: featured.logo_url ? `url(${proxyAsset(featured.logo_url)})` : undefined,
              backgroundColor: featuredAccent,
            }}
          >
            {!featured.logo_url && (
              <span className="splash-display text-7xl md:text-8xl text-[#0d100d]" aria-hidden="true">
                {(featured.business_name || "?")[0].toUpperCase()}
              </span>
            )}
          </div>
          {/* Body */}
          <div className="md:col-span-8 p-6 md:p-8">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="splash-mono uppercase tracking-widest text-[10px] px-2 py-1 border-2"
                style={{ color: featuredAccent, borderColor: featuredAccent }}
              >
                {featured.package === "city_takeover" ? "City Takeover" : (featured.package || "Featured")}
              </span>
              {featured.city && (
                <span className="splash-mono uppercase tracking-widest text-[10px] text-[#0d100d]/55">
                  · {featured.city}
                </span>
              )}
            </div>
            <h3 className="splash-display text-3xl md:text-4xl uppercase mt-3 leading-tight">
              {featured.business_name}
            </h3>
            {featured.description && (
              <p className="mt-3 text-[14px] md:text-[15px] text-[#0d100d]/75 leading-relaxed line-clamp-3">
                {featured.description}
              </p>
            )}
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              {featured.id && (
                <Link
                  to={`/sponsors/${featured.id}`}
                  className="btn-loot-solid"
                  data-testid="splash-sponsor-featured-cta"
                >
                  Visit sponsor →
                </Link>
              )}
              <Link
                to="/sponsors"
                className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#0d100d]/70 hover:text-[#0d100d]"
                data-testid="splash-sponsor-see-all"
              >
                See all {sponsors.length} sponsors →
              </Link>
            </div>
          </div>
        </article>

        {/* Marquee row of every sponsor — secondary glance */}
        {sponsors.length > 1 && (
          <div className="mt-8 md:mt-10">
            <div className="splash-mono uppercase tracking-[0.22em] text-[10px] text-[#0d100d]/55 mb-3">
              Other supporters
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {sponsors.map((sp, i) => {
                const tierAccent = sp.package === "city_takeover" ? "#d7a62a" : "#76B82A";
                return (
                  <Link
                    key={sp.id || i}
                    to={sp.id ? `/sponsors/${sp.id}` : "/sponsors"}
                    className={`bg-[#fffaef] border-2 border-[#0d100d] p-3 md:p-4 flex flex-col items-center gap-2 hover:bg-[#f3edd9] transition ${i === (idx % sponsors.length) ? "shadow-[4px_4px_0_#0d100d]" : ""}`}
                    onMouseEnter={() => setIdx(i)}
                    onFocus={() => setIdx(i)}
                    data-testid={`splash-sponsor-tile-${i}`}
                  >
                    <div
                      className="w-12 h-12 rounded-full bg-cover bg-center border-2 border-[#0d100d] flex items-center justify-center shrink-0"
                      style={{
                        backgroundImage: sp.logo_url ? `url(${proxyAsset(sp.logo_url)})` : undefined,
                        backgroundColor: tierAccent,
                      }}
                    >
                      {!sp.logo_url && (
                        <span className="splash-display text-lg text-[#0d100d]">
                          {(sp.business_name || "?")[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="text-center min-w-0 w-full">
                      <div className="splash-display text-sm md:text-base uppercase leading-tight truncate">
                        {sp.business_name}
                      </div>
                      {sp.city && (
                        <div className="splash-mono text-[10px] uppercase tracking-widest text-[#0d100d]/50 truncate">
                          {sp.city}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Trail-CTA for would-be sponsors */}
        <div className="mt-8 md:mt-10 text-center">
          <Link
            to={s.sponsor_cta_href || "/sponsor-apply"}
            className="btn-loot-solid"
            data-testid="splash-sponsor-spotlight-cta"
          >
            Become a sponsor →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                          DUAL CTA ROW                                  */
/* ===================================================================== */

function DualCTA({ s }) {
  const perfect = Array.isArray(s.sponsor_perfect_for) ? s.sponsor_perfect_for : [];
  return (
    <section className="map-bg py-16 md:py-24" data-testid="splash-dualcta">
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
        {/* Sponsor */}
        <div className="lg:col-span-7" data-testid="splash-sponsor-block">
          <h2 className="splash-display text-3xl md:text-5xl uppercase">
            {s.sponsor_overline || "Sponsor a drop"}
          </h2>
          <div className="splash-script text-2xl md:text-3xl text-[#76B82A] mt-2">
            {s.sponsor_question}
          </div>
          <p className="mt-4 text-[15px] text-[#f7f3e6]/80 max-w-xl leading-relaxed">
            {s.sponsor_body}
          </p>
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div>
              <Link
                to={s.sponsor_cta_href || "/sponsor-apply"}
                className="btn-loot-solid"
                data-testid="splash-become-sponsor-btn"
              >
                {s.sponsor_cta_label || "Become a sponsor"}
                <ArrowRight size={16} />
              </Link>
            </div>
            {perfect.length > 0 && (
              <div className="notepad" data-testid="splash-perfect-for">
                <div className="splash-display text-xl uppercase mb-3">
                  {s.sponsor_perfect_for_title || "Perfect for:"}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {perfect.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-[14px]">
                      <Icon name={p.icon} size={14} className="text-[#0d100d]/60 shrink-0" />
                      <span>{p.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pitch */}
        <div className="lg:col-span-5" data-testid="splash-pitch-block">
          <div className="relative">
            <MapPin
              size={48}
              className="absolute -top-2 -right-2 text-[#76B82A]/60"
              aria-hidden="true"
            />
            <h2 className="splash-display text-3xl md:text-5xl uppercase leading-tight">
              {s.pitch_overline || "Bring The Local Loot to your town"}
            </h2>
          </div>
          <div className="splash-script text-2xl md:text-3xl text-[#76B82A] mt-2">
            {s.pitch_question}
          </div>
          <p className="mt-4 text-[15px] text-[#f7f3e6]/80 max-w-md leading-relaxed">
            {s.pitch_body}
          </p>
          <Link
            to={s.pitch_cta_href || "/vote"}
            className="btn-loot-solid mt-6"
            data-testid="splash-pitch-cta-btn"
          >
            {s.pitch_cta_label || "Pitch your city"}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                          DAILY QUOTE BAND                              */
/* ===================================================================== */

function QuoteBand({ s, quote }) {
  if (s.quote_band_show === false) return null;
  const text = quote?.quote || quote?.text || s.quote_band_fallback;
  const author = quote?.author || s.quote_band_attribution;
  if (!text) return null;
  return (
    <section className="quote-band py-10 md:py-14" data-testid="splash-quote-band">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <Quote size={28} className="mx-auto text-[#76B82A] opacity-80" aria-hidden="true" />
        {s.quote_band_overline && (
          <div className="splash-mono uppercase tracking-[0.28em] text-[10px] md:text-[11px] text-[#76B82A] mt-2">
            {s.quote_band_overline}
          </div>
        )}
        <blockquote className="mt-3 splash-script text-3xl md:text-4xl leading-snug text-[#f7f3e6]">
          “{text}”
        </blockquote>
        {author && (
          <div className="splash-mono text-[12px] md:text-sm text-[#f7f3e6]/60 mt-3">
            {author}
          </div>
        )}
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                              FOOTER                                    */
/* ===================================================================== */

function Footer({ s }) {
  const socials = Array.isArray(s.footer_socials) ? s.footer_socials : [];
  const legal = Array.isArray(s.footer_legal_links) ? s.footer_legal_links : [];
  return (
    <footer className="splash-footer pt-12 pb-8" data-testid="splash-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-12 gap-8 md:gap-10 items-start">
        <div className="md:col-span-3">
          <div className="splash-display uppercase text-xl text-[#f7f3e6]">
            {s.footer_overline || "Stay ready"}
          </div>
          <div className="splash-script text-2xl text-[#76B82A] mt-1">
            Drops can happen anytime.
          </div>
          <p className="mt-3 text-[13px] text-[#f7f3e6]/70 max-w-xs leading-relaxed">
            {s.footer_brand_blurb}
          </p>
        </div>

        <div className="md:col-span-4">
          <div className="flex items-center gap-6 md:gap-7">
            {socials.map((sc, i) => (
              <a
                key={i}
                href={sc.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={sc.label}
                className="flex flex-col items-center gap-1 group"
                data-testid={`splash-social-${i}`}
              >
                <span className="w-10 h-10 rounded-full bg-[#76B82A] text-[#0a0d09] flex items-center justify-center group-hover:bg-[#84d130] transition">
                  <Icon name={sc.icon} size={18} />
                </span>
                <span className="splash-mono text-[10px] uppercase tracking-widest text-[#f7f3e6]/65">
                  {sc.label}
                </span>
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3 splash-script text-xl md:text-2xl text-[#f7f3e6] leading-snug">
          {s.footer_mission}
        </div>

        <div className="md:col-span-2 md:text-right">
          {s.logo_image_url && (
            <img
              src={proxyAsset(s.logo_image_url)}
              alt={s.logo_alt || "The Local Loot"}
              className="h-14 w-auto md:ml-auto"
            />
          )}
          <div className="splash-script text-[#76B82A] text-lg mt-1">
            {s.hero_tagline || "Live Life. Do Good. Be Happy."}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-10 pt-5 border-t border-[#f7f3e6]/10 flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-[12px] text-[#f7f3e6]/55">
        <div className="splash-mono">{s.footer_copyright}</div>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {legal.map((l, i) => (
            <Link key={i} to={l.href} className="hover:text-[#76B82A] uppercase tracking-wider">
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}

/* ===================================================================== */
/*                     COMMUNITY MISSIONS CTA                             */
/* ===================================================================== */

function MissionsCTA({ missions, missionStats }) {
  // Master toggle controls visibility — hide entirely when disabled.
  if (!missions || missions.cta_show === false || missions.missions_enabled === false) return null;
  const total = missionStats?.good_deeds_total ?? 0;
  const legends = missionStats?.loot_legends_total ?? 0;
  const active = missionStats?.active_missions ?? 0;
  return (
    <section className="cream-section py-16 md:py-24" data-testid="splash-missions-cta">
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
        <div className="splash-mono uppercase tracking-[0.28em] text-[11px] text-[#4f8c19]">
          {missions.cta_overline || "Live life. Do good. Be happy."}
        </div>
        <h2 className="splash-display text-4xl md:text-6xl uppercase mt-3 leading-[0.95]">
          {(missions.cta_headline || "Community missions.").split(" ").map((w, i, arr) => (
            <span key={i}>
              {i === arr.length - 1 ? <span style={{ color: "#76B82A" }}>{w}</span> : w}
              {i < arr.length - 1 ? " " : ""}
            </span>
          ))}
        </h2>
        <p className="mt-4 max-w-2xl mx-auto text-[15px] md:text-base text-[#0d100d]/75 leading-relaxed">
          {missions.cta_subhead}
        </p>
        {(total > 0 || legends > 0 || active > 0) && (
          <div className="mt-7 flex flex-wrap justify-center gap-x-10 gap-y-3">
            {active > 0 && (
              <div>
                <div className="splash-display text-3xl md:text-4xl text-[#0d100d]">
                  {active}
                </div>
                <div className="splash-mono text-[11px] uppercase tracking-widest text-[#0d100d]/55">
                  active mission{active === 1 ? "" : "s"}
                </div>
              </div>
            )}
            <div>
              <div className="splash-display text-3xl md:text-4xl text-[#76B82A]">
                {total.toLocaleString()}
              </div>
              <div className="splash-mono text-[11px] uppercase tracking-widest text-[#0d100d]/55">
                {missions.ticker_total_label || "good deeds done"}
              </div>
            </div>
            {legends > 0 && (
              <div>
                <div className="splash-display text-3xl md:text-4xl" style={{ color: "#d7a62a" }}>
                  {legends.toLocaleString()}
                </div>
                <div className="splash-mono text-[11px] uppercase tracking-widest text-[#0d100d]/55">
                  {missions.ticker_legends_label || "Loot Legends crowned"}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt-8 flex flex-wrap justify-center items-center gap-3">
          <Link to="/missions" className="btn-paper-light" data-testid="splash-missions-cta-btn">
            {missions.cta_button_label || "See this week's mission"}
            <ArrowRight size={16} />
          </Link>
          {total > 0 && (
            <Link to="/good-deeds" className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#4f8c19] hover:text-[#76B82A] inline-flex items-center gap-1">
              {missions.ticker_view_all_label || "See the wall"} <ArrowRight size={12} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                       LOOT MASTER CTA                                  */
/* ===================================================================== */

function LootMasterCTA({ lm }) {
  // Render only when settings are loaded AND recruitment is open. The
  // /lootmaster page has its own gate, this component is just a hook.
  if (!lm || lm.recruitment_open === false) return null;
  return (
    <section className="lm-cta-section py-16 md:py-24" data-testid="splash-lootmaster-cta">
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
        <div className="splash-mono uppercase tracking-[0.28em] text-[11px] text-[#76B82A]">
          {lm.cta_overline || "Calling all leaders"}
        </div>
        <h2 className="splash-display text-4xl md:text-6xl uppercase mt-4 leading-[0.95]">
          {(lm.cta_headline || "Become a Loot Master.").split(" ").map((w, i, arr) => (
            <span key={i}>
              {i === arr.length - 1 ? <span className="headline-stamp--green">{w}</span> : w}
              {i < arr.length - 1 ? " " : ""}
            </span>
          ))}
        </h2>
        <p className="mt-5 max-w-2xl mx-auto text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed">
          {lm.cta_subhead}
        </p>
        <div className="mt-8 flex flex-wrap justify-center items-center gap-4">
          <Link
            to="/lootmaster"
            className="btn-loot-solid"
            data-testid="splash-lootmaster-cta-btn"
          >
            {lm.cta_button_label || "Learn more & apply"}
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ===================================================================== */
/*                       MOBILE STICKY CTA BAR                            */
/* ===================================================================== */

function MobileStickyCTA({ s }) {
  return (
    <div className="mobile-sticky-cta" data-testid="splash-mobile-sticky">
      <Link
        to={s.hero_primary_cta_href || "/home"}
        className="btn-loot-solid flex-1 justify-center text-xs"
        style={{ padding: "0.7rem 0.8rem" }}
        data-testid="splash-mobile-primary"
      >
        {s.hero_primary_cta_label || "Start hunting"}
        <MapPin size={14} />
      </Link>
      <Link
        to={s.hero_secondary_cta_href || "/register"}
        className="btn-loot-outline flex-1 justify-center text-xs"
        style={{ padding: "0.7rem 0.8rem" }}
        data-testid="splash-mobile-secondary"
      >
        {s.hero_secondary_cta_label || "Register now"}
      </Link>
    </div>
  );
}

